// A Fleet Live panel's show and hide (#6778, prototype `powerOff` / `powerOn`, `:4108-4133`):
// hiding collapses the panel to a 2 px line in 420 ms, then to nothing in 300 ms; showing
// draws the line out in 280 ms, then opens it in 520 ms. Web Animations only, so nothing
// here holds a timer. Under reduced motion, or where `Element.animate` is missing (the test
// DOM), the panel shows and hides at once.

const IN_EASE = 'cubic-bezier(0.16,1,0.3,1)';
const OUT_EASE = 'cubic-bezier(0.7,0,0.84,0)';

export const POWER_MS = { offCollapse: 420, offFold: 300, onLine: 280, onOpen: 520 } as const;

/** Whether `el` can run the power animation. */
export function canAnimate(el: Element | undefined): el is HTMLElement {
  return el !== undefined && typeof (el as HTMLElement).animate === 'function';
}

/** Collapse `el`, then call `done` (which hides it). */
export function powerOff(el: HTMLElement, done: () => void): void {
  const h = el.offsetHeight;
  el.classList.add('fleet-live__wrap--power');
  const a1 = el.animate(
    [
      { height: `${h}px`, opacity: 1, filter: 'brightness(1)' },
      { height: `${h}px`, opacity: 1, filter: 'brightness(1.8)', offset: 0.08 },
      { height: `${h}px`, opacity: 0.7, filter: 'brightness(1)', offset: 0.14 },
      { height: '2px', opacity: 1, filter: 'brightness(1.4)' },
    ],
    { duration: POWER_MS.offCollapse, easing: OUT_EASE, fill: 'forwards' },
  );
  a1.onfinish = () => {
    const a2 = el.animate(
      [
        { height: '2px', transform: 'scaleX(1)', opacity: 1 },
        { height: '2px', transform: 'scaleX(0.002)', opacity: 0.9, offset: 0.85 },
        { height: '2px', transform: 'scaleX(0)', opacity: 0 },
      ],
      { duration: POWER_MS.offFold, easing: OUT_EASE, fill: 'forwards' },
    );
    a2.onfinish = () => {
      a1.cancel();
      a2.cancel();
      el.classList.remove('fleet-live__wrap--power');
      done();
    };
  };
}

/** Open `el`, already un-hidden. */
export function powerOn(el: HTMLElement): void {
  const h = el.offsetHeight;
  el.classList.add('fleet-live__wrap--power');
  const a1 = el.animate(
    [
      { height: '2px', transform: 'scaleX(0)', opacity: 0 },
      { height: '2px', transform: 'scaleX(1)', opacity: 1 },
    ],
    { duration: POWER_MS.onLine, easing: IN_EASE },
  );
  a1.onfinish = () => {
    const a2 = el.animate(
      [
        { height: '2px', filter: 'brightness(1.6)' },
        { height: `${h}px`, filter: 'brightness(1.15)', offset: 0.7 },
        { height: `${h}px`, filter: 'brightness(1)' },
      ],
      { duration: POWER_MS.onOpen, easing: IN_EASE },
    );
    a2.onfinish = () => el.classList.remove('fleet-live__wrap--power');
  };
}
